import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import './Login.css';

const API_URL = window.location.origin;

function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('idle'); // idle | sending | sent | error
  const [forgotMessage, setForgotMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reset') === 'success') {
      setSuccessMessage('Your password has been reset. You can now sign in.');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      localStorage.setItem('token', response.data.token);
      if (response.data.user) {
        localStorage.setItem('businessId', response.data.user.businessId);
        if (response.data.user.apiKey) {
          localStorage.setItem('apiKey', response.data.user.apiKey);
        }
        if (response.data.user.businessName) {
          localStorage.setItem('businessName', response.data.user.businessName);
        }
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotStatus('sending');
    setForgotMessage('');
    try {
      const res = await axios.post(`${API_URL}/forgot-password`, { email: forgotEmail });
      setForgotStatus('sent');
      setForgotMessage(res.data.message || 'Check your email for a reset link.');
    } catch (err) {
      setForgotStatus('error');
      setForgotMessage(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
  };

  if (forgotMode) {
    return (
      <div className="login-page">
        <div className="login-brand-panel">
          <div className="login-brand-content">
            <div className="login-brand-logo">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="10" fill="rgba(255,255,255,0.2)" />
                <path d="M12 28V12h6l4 8 4-8h6v16h-5V19l-3.5 7h-3L17 19v9h-5z" fill="white" />
              </svg>
              <span className="login-brand-name">Chatflo</span>
            </div>
            <h1 className="login-brand-tagline">Reset your password</h1>
            <p className="login-brand-subtitle">Enter your email and we'll send you a link to reset your password.</p>
          </div>
        </div>

        <div className="login-form-panel">
          <div className="login-form-wrapper">
            <div className="login-form-header">
              <h2>Forgot password</h2>
              <p>Enter your account email address below</p>
            </div>

            {forgotStatus === 'sent' ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 18px', color: '#166534', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
                {forgotMessage}
              </div>
            ) : (
              <>
                {forgotStatus === 'error' && (
                  <div className="login-error">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M8 4.5v4M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    {forgotMessage}
                  </div>
                )}
                <form onSubmit={handleForgotSubmit} className="login-form">
                  <div className="login-field">
                    <label htmlFor="forgotEmail">Email address</label>
                    <input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      autoFocus
                    />
                  </div>
                  <button type="submit" disabled={forgotStatus === 'sending'} className="login-submit">
                    {forgotStatus === 'sending' ? <span className="login-spinner" /> : null}
                    {forgotStatus === 'sending' ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              </>
            )}

            <p className="login-switch">
              <button
                type="button"
                onClick={() => { setForgotMode(false); setForgotStatus('idle'); setForgotMessage(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', fontWeight: 600, padding: 0 }}
              >
                Back to sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-brand-panel">
        <div className="login-brand-content">
          <div className="login-brand-logo">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="10" fill="rgba(255,255,255,0.2)" />
              <path d="M12 28V12h6l4 8 4-8h6v16h-5V19l-3.5 7h-3L17 19v9h-5z" fill="white" />
            </svg>
            <span className="login-brand-name">Chatflo</span>
          </div>
          <h1 className="login-brand-tagline">AI that works your leads while you work the job.</h1>
          <p className="login-brand-subtitle">Chatflo is an AI-powered chat and lead follow-up system built for service businesses. It captures, qualifies, and books leads automatically — 24/7.</p>
          <div className="login-brand-illustration">
            <svg width="280" height="200" viewBox="0 0 280 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="40" y="40" width="200" height="120" rx="12" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
              <rect x="60" y="60" width="80" height="8" rx="4" fill="rgba(255,255,255,0.3)" />
              <rect x="60" y="76" width="120" height="8" rx="4" fill="rgba(255,255,255,0.2)" />
              <rect x="60" y="92" width="100" height="8" rx="4" fill="rgba(255,255,255,0.2)" />
              <rect x="60" y="116" width="60" height="28" rx="8" fill="rgba(255,255,255,0.25)" />
              <circle cx="220" cy="80" r="24" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
              <path d="M212 80l5 5 10-10" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>

      <div className="login-form-panel">
        <div className="login-form-wrapper">
          <div className="login-form-header">
            <h2>Welcome back</h2>
            <p>Sign in to your account to continue</p>
          </div>

          {successMessage && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', color: '#166534', fontSize: '0.875rem', marginBottom: 16 }}>
              {successMessage}
            </div>
          )}

          {error && (
            <div className="login-error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 4.5v4M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="login-field">
              <div className="login-field-header">
                <label htmlFor="password">Password</label>
                <a
                  href="#forgot"
                  className="login-forgot"
                  onClick={(e) => { e.preventDefault(); setForgotMode(true); setForgotEmail(formData.email); }}
                >
                  Forgot password?
                </a>
              </div>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
              />
            </div>

            <button type="submit" disabled={loading} className="login-submit">
              {loading ? (
                <span className="login-spinner" />
              ) : null}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="login-switch">
            Don't have an account? <Link to="/register">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
